import { z } from 'zod'

import { isNull } from '../../util'
import { csvColumnLabelFor } from '../csvColumnLabel'

import { normalizeReferenceNumber } from './normalizeReferenceNumber'

//
// デリバリー管理表 CSV（ロール品・確定フォーマット 12 列）の行バリデーション・正規化。
// 仕様: docs/domain/order_matching/05_csv_specs.md §1（2026/07/27 受領の確定フォーマットで確定。
// 12 列目に品目番号が追加された。旧 11 列 CSV も列が無いだけで取り込める）
// - 必須は契約番号・員数・重量。その他の列は空欄を許容して null に寄せる
// - Excel からの CSV 保存では最大行までの空行（`,,,,,,,,,,`）が末尾に大量に付くため、
//   全列が空の行はエラーにせずスキップする（実サンプルは 20,001 行中データ 32 行）
//

export const deliveryTableCsvColumnMapper = {
  contractNumber: '契約番号',
  constructionName: '工事名称',
  manufacturerName: 'メーカー名', // 画面表示名は「製造会社名」（07/06 版で呼称統一）だが、CSV の列名は「メーカー名」のまま
  productName: '品名',
  standard: '規格',
  size: 'サイズ',
  length: '長さ',
  quantity: '員数',
  weight: '重量',
  constructionNumber: '工事番号',
  managementNumber: '管理番号',
  itemNumber: '品目番号',
}

// 空欄を null に寄せる任意入力の文字列列
const optionalStringColumn = z
  .string()
  .optional()
  .transform((x) => {
    if (isNull(x)) {
      return null
    }
    const trimmed = x.trim()
    return trimmed === '' ? null : trimmed
  })

// カンマ区切り（3 桁区切りのみ）を許容する必須の整数列。
// `/^[0-9,]+$/` だと `1,2,3` や `,,`（→ 0）のような不正値を通してしまい、
// 重量照合の入力が壊れるため、桁区切りの位置まで検証する
const requiredIntegerColumn = z.preprocess(
  (v) => (typeof v === 'number' ? String(v) : (v ?? '')),
  z.string().transform((x, ctx) => {
    const trimmed = x.trim()
    if (!/^\d+$/.test(trimmed) && !/^\d{1,3}(,\d{3})+$/.test(trimmed)) {
      ctx.addIssue({ code: 'custom', message: '整数で入力してください。' })
      return z.NEVER
    }
    return Number(trimmed.replace(/,/g, ''))
  }),
)

export function validateAndNormalizeDeliveryTableCsvRecords({
  record,
  index,
}: {
  record: Record<string, string | number | null>
  index: number
}) {
  // 全列が空の行はスキップ（Excel エクスポート由来の末尾空行）
  if (Object.values(record).every((x) => x === '' || isNull(x))) {
    return {
      success: false as const,
      isEmptyLine: true as const,
      message: `空行です。`,
      record,
      index,
    }
  }

  const data = {
    contractNumber: record[deliveryTableCsvColumnMapper.contractNumber],
    constructionName: record[deliveryTableCsvColumnMapper.constructionName],
    manufacturerName: record[deliveryTableCsvColumnMapper.manufacturerName],
    productName: record[deliveryTableCsvColumnMapper.productName],
    standard: record[deliveryTableCsvColumnMapper.standard],
    size: record[deliveryTableCsvColumnMapper.size],
    length: record[deliveryTableCsvColumnMapper.length],
    quantity: record[deliveryTableCsvColumnMapper.quantity],
    weight: record[deliveryTableCsvColumnMapper.weight],
    constructionNumber: record[deliveryTableCsvColumnMapper.constructionNumber],
    managementNumber: record[deliveryTableCsvColumnMapper.managementNumber],
    itemNumber: record[deliveryTableCsvColumnMapper.itemNumber],
  }

  const schema = z.object({
    // 契約番号は発注（親）のキー。正規化（全角/半角統一・大文字化・trim）した値で保存し、
    // その値同士の完全一致で紐付け・洗い替えの単位にする（原文は raw に残る）。
    // csv-parse の設定次第で数値として渡りうるため、文字列へ寄せてから検証する
    contractNumber: z.preprocess(
      (v) => (typeof v === 'number' ? String(v) : (v ?? '')),
      z
        .string()
        .transform((x) => normalizeReferenceNumber(x))
        .pipe(z.string().min(1, { message: '契約番号は必須です。' })),
    ),
    constructionName: optionalStringColumn,
    manufacturerName: optionalStringColumn,
    productName: optionalStringColumn,
    standard: optionalStringColumn,
    size: optionalStringColumn,
    length: optionalStringColumn,
    quantity: requiredIntegerColumn,
    weight: requiredIntegerColumn,
    constructionNumber: optionalStringColumn,
    managementNumber: optionalStringColumn,
    itemNumber: optionalStringColumn,
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          const label = csvColumnLabelFor(deliveryTableCsvColumnMapper, x.path.join(''))
          const value = data[x.path.join('') as keyof typeof data]
          return `${label}: ${x.message}(入力された値: ${value})`
        })
        .join('。'),
      record,
      index,
    }
  }

  const result = {
    ...parsed.data,
    rank: index + 1,
    raw: record,
  }

  return {
    success: true as const,
    record,
    index,
    result,
    resultTitle: result.contractNumber,
  }
}
